import {
  ICeleb,
  IComparisonReportData,
  ILookalikeReportData,
} from '@api/requests';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  getCompareImageFromSessionStorage,
  getCompareImageInfoFromSessionStorage,
  getCompareRequestIdFromSessionStorage,
  getLookalikeReportDataFromSessionStorage,
  getLookalikeReportIdFromSessionStorage,
  getReportDataFromSessionStorage,
  getReportIdFromSessionStorage,
  getSelectedComparisonFromSessionStorage,
  getSelfieFromSessionStorage,
  getUserNameFromSessionStorage,
  saveCompareImageInfoToSessionStorage,
  saveCompareImageToSessionStorage,
  saveComparisonReportIdToSessionStorage,
  saveLookalikeReportDataToSessionStorage,
  saveLookalikeReportIdToSessionStorage,
  saveReportDataToSessionStorage,
  saveSelectedComparisonToSessionStorage,
  saveSelfieToSessionStorage,
  saveUserNameToSessionStorage,
} from '@utils/storage';

interface OnboardingState {
  selfie: string | null;
  compareImage: string | null;
  compareRequestId: string;
  selectedComparison: string | null;
  comparisonReportId: string | null;
  comparisonReportData: IComparisonReportData | null;
  name: string | null;
  compareImageInfo: ICeleb | null;
  lookalikeReportId: string | null;
  lookalikeReportData: ILookalikeReportData | null;
  fallbackCompareReportImages: string[] | null;
  lastMaskResults: any | null;
}

const initialState: OnboardingState = {
  selfie: getSelfieFromSessionStorage(),
  compareImage: getCompareImageFromSessionStorage(),
  compareRequestId: getCompareRequestIdFromSessionStorage(),
  selectedComparison: getSelectedComparisonFromSessionStorage(),
  comparisonReportId: getReportIdFromSessionStorage(),
  comparisonReportData: getReportDataFromSessionStorage(),
  name: getUserNameFromSessionStorage(),
  compareImageInfo: getCompareImageInfoFromSessionStorage(),
  lookalikeReportId: getLookalikeReportIdFromSessionStorage(),
  lookalikeReportData: getLookalikeReportDataFromSessionStorage(),
  fallbackCompareReportImages: null,
  lastMaskResults: null,
};

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    setPhoto(state, action: PayloadAction<string>) {
      state.selfie = action.payload;
      saveSelfieToSessionStorage(action.payload);
    },
    setCompareImage(state, action: PayloadAction<string>) {
      state.compareImage = action.payload;
      saveCompareImageToSessionStorage(action.payload);
    },
    setSelectedComparison(state, action: PayloadAction<string>) {
      state.selectedComparison = action.payload;
      saveSelectedComparisonToSessionStorage(action.payload);
    },
    setComparisonReportId(state, action: PayloadAction<string>) {
      state.comparisonReportId = action.payload;
      saveComparisonReportIdToSessionStorage(action.payload);
    },
    setComparisonReportData(
      state,
      action: PayloadAction<IComparisonReportData>,
    ) {
      state.comparisonReportData = action.payload;
      saveReportDataToSessionStorage(action.payload);
    },
    setLookalikeReportId(state, action: PayloadAction<string>) {
      state.lookalikeReportId = action.payload;
      saveLookalikeReportIdToSessionStorage(action.payload);
    },
    setLookalikeReportData(state, action: PayloadAction<ILookalikeReportData>) {
      state.lookalikeReportData = action.payload;
      saveLookalikeReportDataToSessionStorage(action.payload);
    },
    clearPhoto(state) {
      state.selfie = null;
      saveSelfieToSessionStorage(null);
    },
    setName(state, action: PayloadAction<string>) {
      state.name = action.payload;
      saveUserNameToSessionStorage(action.payload);
    },
    setCompareImageInfo(state, action: PayloadAction<ICeleb>) {
      state.compareImageInfo = action.payload;
      saveCompareImageInfoToSessionStorage(action.payload);
    },
    setFallbackCompareReportImages(state, action: PayloadAction<string[]>) {
      state.fallbackCompareReportImages = action.payload;
    },
    setLastMaskResults(state, action: PayloadAction<any>) {
      state.lastMaskResults = action.payload;
    },
  },
});

export const {
  setPhoto,
  clearPhoto,
  setCompareImage,
  setSelectedComparison,
  setComparisonReportId,
  setComparisonReportData,
  setName,
  setCompareImageInfo,
  setLookalikeReportId,
  setLookalikeReportData,
  setFallbackCompareReportImages,
  setLastMaskResults,
} = onboardingSlice.actions;
export default onboardingSlice.reducer;
